import type { TimezoneEngineConfig } from "@multisystemsuite/timezone-engine-shared-types";
import type {
  ConvertTimezoneOptions,
  FormatDateOptions,
} from "@multisystemsuite/timezone-engine-shared-types";
import {
  ConfigManager,
  PluginManager,
  TimezoneCache,
  createConsoleLogger,
} from "@multisystemsuite/timezone-engine-utils";
import {
  getCountryTimezones,
  getCityTimezone,
  getTimezoneInfo,
} from "@multisystemsuite/timezone-engine-world-data";
import { convertTimezone as convertTimezoneCore } from "./conversion.js";
import { formatDate as formatDateCore } from "./formatting.js";

/** Enterprise timezone engine with config, cache, and plugin support */
export class TimezoneEngine {
  readonly config: ConfigManager;
  readonly plugins: PluginManager;
  readonly cache: TimezoneCache;

  constructor(config: TimezoneEngineConfig = {}) {
    this.config = new ConfigManager(config);
    this.plugins = new PluginManager();
    this.cache = new TimezoneCache(this.config.get().cacheTTL);

    const cfg = this.config.get();
    if (cfg.plugins) {
      for (const plugin of cfg.plugins) {
        this.plugins.register(plugin);
      }
    }
    this.plugins.init(cfg);
  }

  formatDate(date: Date | string | number, options: FormatDateOptions = {}): string {
    const d = date instanceof Date ? date : new Date(date);
    const cfg = this.config.get();
    const baseOptions: FormatDateOptions = {
      timezone: cfg.defaultTimezone ?? "UTC",
      locale: cfg.defaultLocale ?? "en-US",
      ...options,
    };
    if (cfg.defaultFormat) {
      baseOptions.format = options.format ?? cfg.defaultFormat;
    }
    const resolvedOptions = this.plugins.runBeforeFormat(d, baseOptions);
    const result = formatDateCore(d, resolvedOptions);
    return this.plugins.runAfterFormat(d, result);
  }

  convertTimezone(options: ConvertTimezoneOptions): Date {
    const resolved = this.plugins.runBeforeConvert(options);
    return convertTimezoneCore(resolved);
  }

  getCountryTimezones(countryCode: string) {
    return getCountryTimezones(countryCode);
  }

  getCityTimezone(city: string) {
    return getCityTimezone(city);
  }

  getTimezoneInfo(timezone: string) {
    return getTimezoneInfo(timezone);
  }

  enableLogging(): void {
    this.config.update({ logger: createConsoleLogger() });
  }
}

/** Create a configured timezone engine instance */
export function createTimezoneEngine(config?: TimezoneEngineConfig): TimezoneEngine {
  return new TimezoneEngine(config);
}

/** Default singleton engine instance */
export const defaultEngine = createTimezoneEngine();
